import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  convertDateIntoCompactFormat,
  convertDateIntoLocalDate,
} from '../../common/Functions';
import TagsHandler from '../TagsHandler';
import ActionBoostBlock from './subComponents/ActionBoostBlock';
import ActionCell from './subComponents/ActionCell';
import EptFeedback from './subComponents/EptFeedback';
import EptFeedbackSources from './subComponents/EptFeedbackSources';
import EptTableAssessment from './subComponents/EptTableAssesment';
import { Column, TableBodyProps } from './types';

const TableBody: React.FC<TableBodyProps> = ({
  data,
  columns,
  loading,
  onRowClick,
  generateRowUrl,
  rowClickAction,
  onDataReload,
  responseId,
  columnWidths,
  columnMinWidths,
  tableId,
}) => {
  const navigate = useNavigate();

  // Keep these as fallbacks for programmatic navigation if needed
  const handleCellClick = (
    column: Column,
    rowData: any,
    e: React.MouseEvent
  ) => {
    e.stopPropagation(); // Prevent row click when clicking cell
    if (column.isClickable && column.clickAction?.enabled) {
      let redirectPath =
        column.clickAction.redirectTo +
        '/' +
        rowData[column.clickAction.redirect_key];
      navigate(redirectPath);
    }
  };

  const handleRowClickOnColumn = (
    rowData: any,
    column: Column,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();
    if (onRowClick) {
      onRowClick(rowData, column);
    }
  };

  const isColumnClickable = (column: Column): boolean => {
    // Handle both single object and array formats for backward compatibility
    const rowClickActions = Array.isArray(rowClickAction)
      ? rowClickAction
      : rowClickAction
        ? [rowClickAction]
        : [];

    return rowClickActions.some(
      action => action.enabled && action.column_key === column.key
    );
  };

  const isTextColumn = (column: Column): boolean => {
    // Consider a column as text if it's not a special type, not numeric, not an ID, not tags, not file type, and not action
    const specialTypes = [
      'icon',
      'complex_icon',
      'date',
      'compound',
      'array',
      'boolean',
      'link',
    ];
    return (
      !specialTypes.includes(column.type_) &&
      !isNumericColumn(column) &&
      !isIdColumn(column) &&
      !isTagsColumn(column) &&
      !isFileTypeColumn(column) &&
      !isActionColumn(column)
    );
  };

  const isArrayColumn = (column: Column): boolean => {
    return column.type_ === 'array';
  };

  const isTagsColumn = (column: Column): boolean => {
    return column.key === 'tags' || column.key === 'status';
  };

  const isDateColumn = (column: Column): boolean => {
    return column.type_ === 'date';
  };

  const isCompoundColumn = (column: Column): boolean => {
    return column.type_ === 'compound';
  };

  const isBooleanColumn = (column: Column): boolean => {
    return column.type_ === 'boolean';
  };

  const isLinkColumn = (column: Column): boolean => {
    const typeValue = (column.type_ || '').toString().toLowerCase();
    return typeValue === 'link';
  };

  const isIdColumn = (column: Column): boolean => {
    const lowerKey = column.key.toLowerCase();
    // Exclude KS ID from being treated as an ID column
    if (lowerKey.includes('ks') && lowerKey.includes('id')) {
      return false;
    }
    return lowerKey === 'id' || lowerKey.endsWith('_id');
  };

  const isNumericColumn = (column: Column): boolean => {
    // Check if it's explicitly marked as numeric type
    if (column.type_ === 'numeric' || column.type_ === 'number') {
      return true;
    }

    // Check common numeric column patterns (excluding ID columns)
    const numericKeys = [
      'channel',
      'count',
      'uses',
      'ks_id',
      'ksid',
      'ks id',
      'number',
      'amount',
      'price',
      'quantity',
      'total',
    ];
    const lowerKey = column.key.toLowerCase();

    return numericKeys.some(
      pattern => lowerKey.includes(pattern) || lowerKey.endsWith('_number')
    );
  };

  const isFileTypeColumn = (column: Column): boolean => {
    const lowerKey = column.key.toLowerCase();
    // Detect type columns - either just "type" or contains "type"
    return lowerKey === 'type' || lowerKey.includes('type');
  };

  const isActionColumn = (column: Column): boolean => {
    return column.type_ === 'icon' || column.type_ === 'complex_icon';
  };

  const renderCell = (column: Column, rowData: any): JSX.Element => {
    // Handle boost/block specific actions first
    if (
      (column.type_ === 'icon' || column.type_ === 'complex_icon') &&
      column.sub_keys &&
      column.sub_keys.some(
        subKey => subKey.id === 'boost' || subKey.id === 'block'
      )
    ) {
      return (
        <ActionBoostBlock
          column={column}
          rowData={rowData}
          onDataReload={onDataReload}
        />
      );
    }

    // Handle other action columns
    if (column.type_ === 'icon' || column.type_ === 'complex_icon') {
      return (
        <ActionCell
          column={column}
          rowData={rowData}
          onDataReload={onDataReload}
          tableId={tableId}
        />
      );
    }

    let cellContent = rowData[column.key];

    // Handle LINK type columns
    if (isLinkColumn(column) && cellContent) {
      const url = String(cellContent);

      // Special handling for KS items table - check for GKD files
      if (tableId === 'ks_items_table') {
        const itemType = rowData.type;

        // GKD files should open in the GKD editor
        if (itemType === 'gkd' || (url && url.endsWith('.gkd'))) {
          // Extract ks_id from the current URL path
          const ksIdMatch = window.location.pathname.match(
            /\/ks-details\/([^/]+)/
          );
          const ksId = ksIdMatch ? ksIdMatch[1] : null;

          if (ksId) {
            const gkdEditorRoute = `/settings/ks/${ksId}/${url}`;
            return (
              <Link
                to={gkdEditorRoute}
                className="cell-content link-column-content"
                onClick={e => e.stopPropagation()}
                title={url}
              >
                {url}
              </Link>
            );
          }
        }

        // For all other KS items, honor backend-provided row_click_action by
        // substituting placeholders and navigating accordingly
        const actions = Array.isArray(rowClickAction)
          ? rowClickAction
          : rowClickAction
            ? [rowClickAction]
            : [];

        // Prefer action bound to this column; fall back to first enabled action
        const selected =
          actions.find(a => a.enabled && a.column_key === column.key) ||
          actions.find(a => a.enabled);

        if (selected && selected.action_api) {
          // Replace placeholders with row values and current ks_id
          const ksIdMatch = window.location.pathname.match(
            /\/ks-details\/([^/]+)/
          );
          const ksId = ksIdMatch ? ksIdMatch[1] : '';

          const isCannedResponse = selected.action_api.includes(
            '/settings/ks/canned-response/'
          );
          const valueForUrlPlaceholder = isCannedResponse
            ? rowData.title || ''
            : rowData.url || '';

          let actionUrl = selected.action_api
            .replace(/\{url\}/g, encodeURIComponent(valueForUrlPlaceholder))
            .replace(/\{title\}/g, encodeURIComponent(rowData.title || ''))
            .replace(/\{ks_id\}/g, encodeURIComponent(ksId));

          // Convert to relative route for SPA when possible
          const toRelative = (u: string): string => {
            try {
              const parsed = new URL(u);
              return parsed.pathname + parsed.search;
            } catch {
              return u; // already relative
            }
          };

          // Ensure filename param exists for download route to preserve file name
          const ensureFilenameParam = (u: string): string => {
            const isDownload = u.includes('/settings/ks/item/download');
            if (!isDownload) return u;

            const hasFilename = /[?&]filename=/.test(u);
            if (hasFilename) return u;

            // Prefer row title; fallback to basename from URL
            const fallbackFromUrl = () => {
              try {
                const decoded = decodeURIComponent(String(rowData.url || ''));
                const parts = decoded.split('/').filter(Boolean);
                const last = parts[parts.length - 1] || 'file';
                return last;
              } catch {
                return 'file';
              }
            };

            const filename = encodeURIComponent(
              String(rowData.title || fallbackFromUrl())
            );
            const sep = u.includes('?') ? '&' : '?';
            return `${u}${sep}filename=${filename}`;
          };

          const relativeUrl = ensureFilenameParam(toRelative(actionUrl));

          // If backend suggests download, decide: files download, otherwise open raw URL directly
          if (relativeUrl.includes('/settings/ks/item/download')) {
            const urlStr = String(rowData.url || '');
            const lower = urlStr.toLowerCase();
            const fileExts = [
              '.pdf',
              '.txt',
              '.jpg',
              '.jpeg',
              '.png',
              '.gif',
              '.ppt',
              '.pptx',
              '.json',
              '.csv',
              '.xlsx',
              '.doc',
              '.docx',
              '.md',
            ];
            const hasFileExtension = fileExts.some(ext => lower.endsWith(ext));

            if (!hasFileExtension) {
              // Open the raw URL like a normal link (no download)
              return (
                <a
                  href={urlStr}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="cell-content link-column-content"
                  onClick={e => e.stopPropagation()}
                  title={urlStr}
                >
                  {urlStr}
                </a>
              );
            }

            // For file-like URLs, proceed with download flow
            return (
              <a
                href={relativeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="cell-content link-column-content"
                onClick={e => e.stopPropagation()}
                title={url}
              >
                {url}
              </a>
            );
          }

          return (
            <Link
              to={relativeUrl}
              className="cell-content link-column-content"
              onClick={e => e.stopPropagation()}
              title={url}
            >
              {url}
            </Link>
          );
        }
      }

      // Default LINK behavior - standard external link
      return (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="cell-content link-column-content"
          onClick={e => e.stopPropagation()}
          title={url}
        >
          {url}
        </a>
      );
    }

    // Handle tags/status columns specifically
    if (
      (column.key === 'tags' || column.key === 'status') &&
      cellContent !== undefined &&
      cellContent !== null
    ) {
      return (
        <div className="tags-column-content">
          <TagsHandler tags={cellContent} size="small" showType={false} />
        </div>
      );
    }

    // Handle status column with boosted/blocked values as tags
    if (
      column.key === 'status' &&
      cellContent &&
      (cellContent.toLowerCase() === 'boosted' ||
        cellContent.toLowerCase() === 'blocked')
    ) {
      return (
        <div className="status-tag-container">
          <span
            className={`status-tag status-tag-${cellContent.toLowerCase()}`}
          >
            {cellContent}
          </span>
        </div>
      );
    }

    // Handle date formatting
    if (column.type_ === 'date' && cellContent) {
      const fullDate = convertDateIntoLocalDate(cellContent);
      const compactDate = convertDateIntoCompactFormat(cellContent);

      // For date columns, wrap content with appropriate styling
      return (
        <div className="date-column-content" title={fullDate}>
          {compactDate}
        </div>
      );
    }

    // Handle boolean values
    if (column.type_ === 'boolean') {
      // Convert various boolean representations to true/false
      let boolValue = false;
      if (
        cellContent === 1 ||
        cellContent === '1' ||
        cellContent === true ||
        cellContent === 'true'
      ) {
        boolValue = true;
      } else if (
        cellContent === 0 ||
        cellContent === '0' ||
        cellContent === false ||
        cellContent === 'false'
      ) {
        boolValue = false;
      }

      // Generate unique ID for checkbox
      const checkboxId = `${column.key}-${rowData.id || Math.random().toString(36).substr(2, 9)}`;

      // Display as non-editable checkbox
      return (
        <div className="col-xs-12 custom-response-checkbox td-relative">
          <label htmlFor={checkboxId}>
            <span className="checkbox-label"></span>
            <input
              type="checkbox"
              name={`${column.key}-check`}
              id={checkboxId}
              checked={boolValue}
              disabled
              readOnly
            />
            <span className="checkmark"></span>
          </label>
        </div>
      );
    }

    // Handle compound fields (like feedback)
    if (column.type_ === 'compound' && (column.sub_keys || column.subKeys)) {
      if (column.key === 'feedback') {
        return (
          <EptFeedback column={column} data={rowData} label={''} value={0} />
        );
      }
      if (column.key === 'assessment_new') {
        return (
          <EptTableAssessment
            assessment={rowData.assessment_new}
            is_first_three={true}
          />
        );
      }
      if (column.key === 'feedback_sources') {
        return (
          <EptFeedbackSources
            column={column}
            data={rowData}
            responseId={responseId}
          />
        );
      }
    }

    // Handle array type_ - including JSON string arrays
    if (column.type_ === 'array') {
      let arrayData = cellContent;

      // If it's a string that looks like a JSON array, parse it
      if (
        typeof cellContent === 'string' &&
        cellContent.startsWith('[') &&
        cellContent.endsWith(']')
      ) {
        try {
          arrayData = JSON.parse(cellContent);
        } catch (error) {
          console.warn(
            'Failed to parse JSON array string:',
            cellContent,
            error
          );
          // If parsing fails, treat as regular string
          arrayData = cellContent;
        }
      }

      // If we now have an array, render it properly
      if (Array.isArray(arrayData)) {
        return (
          <div className="array-cell">
            {arrayData.map((item, index) => {
              // Special handling for connected channels - make each id clickable
              if (column.key === 'channel_ids') {
                const idVal =
                  typeof item === 'object' && item !== null
                    ? item.channel_id
                    : item;
                const displayVal = idVal ?? '';
                return (
                  <Link
                    key={index}
                    to={`/channels/${displayVal}`}
                    className="array-item"
                    onClick={e => e.stopPropagation()}
                  >
                    {String(displayVal)}
                  </Link>
                );
              }

              let displayValue = item;

              // Handle objects within arrays
              if (typeof item === 'object' && item !== null) {
                // For tags objects, display tag_value if available
                if (item.tag_value) {
                  displayValue = item.tag_value;
                } else if (item.tag_type) {
                  displayValue = item.tag_type;
                } else {
                  // For other objects, try to find a meaningful display value
                  const possibleDisplayKeys = [
                    'name',
                    'label',
                    'title',
                    'value',
                    'text',
                  ];
                  const displayKey = possibleDisplayKeys.find(key => item[key]);
                  displayValue = displayKey
                    ? item[displayKey]
                    : JSON.stringify(item);
                }
              }

              return (
                <span key={index} className="array-item">
                  {displayValue}
                </span>
              );
            })}
          </div>
        );
      }

      // If it's still not an array, just display as text
      return <div className="cell-content">{cellContent}</div>;
    }

    // Apply styling based on column type
    const isText = isTextColumn(column);
    const isNumeric = isNumericColumn(column);
    const isId = isIdColumn(column);

    // For ID columns, wrap content with appropriate styling
    if (isId) {
      const rowUrl = generateRowUrl ? generateRowUrl(rowData, column) : null;
      const isClickableRow = isColumnClickable(column) && rowUrl;

      if (column.isClickable || isClickableRow) {
        const redirectPath = column.isClickable
          ? column.clickAction.redirectTo +
            '/' +
            rowData[column.clickAction.redirect_key]
          : rowUrl;

        return (
          <Link
            to={redirectPath}
            className={`cell-content id-column-content ${column.isClickable ? 'clickable' : 'row-clickable'}`}
            title={cellContent ? String(cellContent) : undefined}
          >
            {cellContent}
          </Link>
        );
      }

      return (
        <div
          className="cell-content id-column-content"
          title={cellContent ? String(cellContent) : undefined}
        >
          {cellContent}
        </div>
      );
    }

    // For numeric columns, wrap content with appropriate styling
    if (isNumeric) {
      const rowUrl = generateRowUrl ? generateRowUrl(rowData, column) : null;
      const isClickableRow = isColumnClickable(column) && rowUrl;

      if (column.isClickable || isClickableRow) {
        const redirectPath = column.isClickable
          ? column.clickAction.redirectTo +
            '/' +
            rowData[column.clickAction.redirect_key]
          : rowUrl;

        return (
          <Link
            to={redirectPath}
            className={`cell-content numeric-column-content ${column.isClickable ? 'clickable' : 'row-clickable'}`}
          >
            {cellContent}
          </Link>
        );
      }

      return (
        <div className="cell-content numeric-column-content">{cellContent}</div>
      );
    }

    // For file type columns, wrap content with appropriate styling
    if (isFileTypeColumn(column)) {
      const rowUrl = generateRowUrl ? generateRowUrl(rowData, column) : null;
      const isClickableRow = isColumnClickable(column) && rowUrl;

      if (column.isClickable || isClickableRow) {
        const redirectPath = column.isClickable
          ? column.clickAction.redirectTo +
            '/' +
            rowData[column.clickAction.redirect_key]
          : rowUrl;

        return (
          <Link
            to={redirectPath}
            className={`cell-content file-type-column-content ${column.isClickable ? 'clickable' : 'row-clickable'}`}
          >
            {cellContent}
          </Link>
        );
      }

      return (
        <div className="cell-content file-type-column-content">
          {cellContent}
        </div>
      );
    }

    // Apply text column styling for text-based columns
    const rowUrl = generateRowUrl ? generateRowUrl(rowData, column) : null;
    const isClickableRow = isColumnClickable(column) && rowUrl;
    const contentClasses = `cell-content ${isText ? 'text-column-content' : ''}`;

    if (column.isClickable || isClickableRow) {
      const redirectPath = column.isClickable
        ? column.clickAction.redirectTo +
          '/' +
          rowData[column.clickAction.redirect_key]
        : rowUrl;

      return (
        <Link
          to={redirectPath}
          className={`${contentClasses} ${column.isClickable ? 'clickable' : 'row-clickable'}`}
          title={isText && cellContent ? String(cellContent) : undefined}
        >
          {cellContent}
        </Link>
      );
    }

    return (
      <div
        className={contentClasses}
        title={isText && cellContent ? String(cellContent) : undefined}
      >
        {cellContent}
      </div>
    );
  };

  if (loading) {
    return (
      <tbody>
        <tr>
          <td colSpan={columns.length} className="loading-cell">
            Loading...
          </td>
        </tr>
      </tbody>
    );
  }

  if (!Array.isArray(data) || data.length === 0) {
    return (
      <tbody>
        <tr>
          <td colSpan={columns.length} className="no-data-cell">
            No data available
          </td>
        </tr>
      </tbody>
    );
  }

  return (
    <tbody>
      {data.map((row, rowIndex) => (
        <tr key={rowIndex}>
          {columns.map((column, colIndex) => {
            const isText = isTextColumn(column);
            const isArray = isArrayColumn(column);
            const isTags = isTagsColumn(column);
            const isDate = isDateColumn(column);
            const isCompound = isCompoundColumn(column);
            const isBoolean = isBooleanColumn(column);
            const isNumeric = isNumericColumn(column);
            const isId = isIdColumn(column);
            const isLink = isLinkColumn(column);
            const isFileType = isFileTypeColumn(column);
            const isAction = isActionColumn(column);

            const cellClasses = [
              isColumnClickable(column) ? 'clickable-column' : '',
              isText ? 'text-column-cell' : '',
              isArray ? 'array-column-cell' : '',
              isTags ? 'tags-column-cell' : '',
              isDate ? 'date-column-cell' : '',
              isCompound ? 'compound-column-cell' : '',
              isBoolean ? 'boolean-column-cell' : '',
              isNumeric ? 'numeric-column-cell' : '',
              isId ? 'id-column-cell' : '',
              isLink ? 'link-column-cell' : '',
              isFileType ? 'file-type-column-cell' : '',
              isAction ? 'action-column-cell' : '',
            ]
              .filter(Boolean)
              .join(' ');

            return (
              <td
                key={colIndex}
                style={
                  columnWidths && columnWidths[column.key]
                    ? {
                        width: columnWidths[column.key],
                        minWidth:
                          columnMinWidths?.[column.key] ||
                          columnWidths[column.key],
                        boxSizing: 'border-box',
                      }
                    : column.width
                      ? {
                          width: column.width,
                          minWidth:
                            columnMinWidths?.[column.key] || column.width,
                          boxSizing: 'border-box',
                        }
                      : {
                          boxSizing: 'border-box',
                          minWidth: columnMinWidths?.[column.key] || '150px',
                        }
                }
                className={cellClasses}
              >
                {renderCell(column, row)}
              </td>
            );
          })}
        </tr>
      ))}
    </tbody>
  );
};

export default TableBody;
